import { Eye, RefreshCw } from 'lucide-react';
import { StatusBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableActionsCell,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import { leadStatus } from '@/theme/status';
import type { LeadCard } from '@/types/crm';
import {
  channelLabel,
  contactLabel,
  describeLeadMotivo,
  describeLeadPreview,
  describeLeadStats,
  formatNumber,
  formatRelativeTime,
  originLabel,
} from './crm-format';

/**
 * La vista de TABLA del CRM (`GET .../crm/leads`).
 *
 * Es el mismo `LeadCard` del kanban, en filas y con el total real de cada estado a la vista: sirve
 * para revisar la lista completa y para encontrar un contacto por su identificador. El kanban y la
 * tabla leen endpoints distintos (el kanban no pagina), así que la pantalla decide cuál se usa.
 *
 * Componente de PROPS (sin hooks) para que el smoke lo pueda pintar con leads inventados.
 */

export interface LeadsTableProps {
  leads: LeadCard[];
  /** Abre la ficha del lead. */
  onOpen: (lead: LeadCard) => void;
  /** Reclasifica el lead a mano. */
  onStatus: (lead: LeadCard) => void;
}

export function LeadsTable({ leads, onOpen, onStatus }: LeadsTableProps) {
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeaderCell>Contacto</TableHeaderCell>
          <TableHeaderCell>Estado</TableHeaderCell>
          <TableHeaderCell>Por qué está ahí</TableHeaderCell>
          <TableHeaderCell>Último mensaje</TableHeaderCell>
          <TableHeaderCell>Actividad</TableHeaderCell>
          <TableHeaderCell align="right">Acciones</TableHeaderCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {leads.map((lead) => {
          const meta = leadStatus[lead.estadoLead];
          const preview = describeLeadPreview(lead);

          return (
            <TableRow key={lead.conversationId}>
              <TableCell>
                <button
                  type="button"
                  onClick={() => onOpen(lead)}
                  className="rounded-control text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                  aria-label={`Ver el lead de ${contactLabel(lead)}`}
                >
                  <span className="block font-medium text-ink">{contactLabel(lead)}</span>
                  <span className="block text-micro text-ink-faint">
                    {channelLabel[lead.channel]} · {originLabel[lead.estadoLeadOrigen]}
                    {lead.requiereAtencion ? ' · requiere atención' : ''}
                  </span>
                </button>
              </TableCell>

              <TableCell>
                <StatusBadge label={meta.label} tone={meta.tone} />
              </TableCell>

              <TableCell className="max-w-xs">
                <span className="block truncate" title={lead.estadoLeadMotivo ?? ''}>
                  {describeLeadMotivo(lead.estadoLeadMotivo)}
                </span>
                <span className="block text-micro text-ink-faint">
                  {describeLeadStats(lead)}
                  {lead.ordenesTotal > 0 ? ` · ${formatNumber(lead.ordenesTotal)} con orden` : ''}
                </span>
              </TableCell>

              <TableCell className="max-w-sm">
                <span className="block truncate" title={preview ?? ''}>
                  {preview ?? 'Sin mensajes todavía'}
                </span>
              </TableCell>

              <TableCell className="whitespace-nowrap">
                {formatRelativeTime(lead.lastMessageAt ?? lead.createdAt)}
              </TableCell>

              <TableActionsCell>
                <span className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Ver el lead de ${contactLabel(lead)}`}
                    onClick={() => onOpen(lead)}
                  >
                    <Eye size={16} />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Reclasificar el lead de ${contactLabel(lead)}`}
                    onClick={() => onStatus(lead)}
                  >
                    <RefreshCw size={16} />
                  </Button>
                </span>
              </TableActionsCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
